import { useCallback, useEffect, useState } from 'react';
import {
  ISelectedDoor,
  ISelectedElement,
  ISelectedHotspot,
  ISelectedModel2D,
  ISelectedPassage,
  ISelectedProduct,
  ISelectedRadiator,
  ISelectedSimpleMeasurement,
  ISelectedSpace,
  ISelectedWall,
  ISelectedWindow,
  ISelectionHandlers
} from '../@types/ContextProviderRelated';
import { EActiveDrawer, EDirection, EInteractionMode } from '../@types/Enums';
import { Side } from '../@types/Side';
import { useToolbarContext } from '../provider/ToolbarProvider';

const emptySelectedWall = {
  wallIndex: -1,
};

const emptyStandardWall = emptySelectedWall;
const emptyPartitionWall = emptySelectedWall;

const emptySelectedWindow = {
  wallIndex: -1,
  windowIndex: -1,
};

const emptySelectedDoor = {
  wallIndex: -1,
  doorIndex: -1,
};

const emptySelectedPassage = {
  wallIndex: -1,
  passageIndex: -1,
};

const emptySelectedRadiator = {
  wallIndex: -1,
  radiatorIndex: -1,
  side: -1
};

const emptySelectedSimpleMeasurement = {
  simpleMeasurementIndex: -1,
};

const emptyProduct = {
  selectedProductIndex: -1,
};

const emptySelectedElement = {
  selectedElementIndex: -1,
};

const emptySelectedModel2D = {
  selectedModel2DIndex: -1,
};

const emptySelectedHotspot = {
  hotspotId: -1,
};

const emptySelectedSpace = {
  spaceId: "-1",
};

export const useSelectionHandlers = (): ISelectionHandlers => {
  const [selectedStandardWall, setSelectedStandardWall] = useState<ISelectedWall>(emptyStandardWall);
  const [selectedPartitionWall, setSelectedPartitionWall] = useState<ISelectedWall>(emptyPartitionWall);
  const [selectedWindow, setSelectedWindow] = useState<ISelectedWindow>(emptySelectedWindow);
  const [selectedDoor, setSelectedDoor] = useState<ISelectedDoor>(emptySelectedDoor);
  const [selectedPassage, setSelectedPassage] = useState<ISelectedPassage>(emptySelectedPassage);
  const [selectedProduct, setSelectedProduct] = useState<ISelectedProduct>(emptyProduct);
  const [selectedRadiator, setSelectedRadiator] = useState<ISelectedRadiator>(emptySelectedRadiator);
  const [selectedElement, setSelectedElement] = useState<ISelectedElement>(emptySelectedElement);
  const [selectedSpace, setSelectedSpace] = useState<ISelectedSpace>(emptySelectedSpace);
  const [selectionAllowed, setSelectionAllowed] = useState<boolean>(true);
  const [activeDrawer, setActiveDrawer] = useState<EActiveDrawer>(EActiveDrawer.NONE);
  const [wallChangeDirection, setWallChangeDirection] = useState<EDirection>(EDirection.BOTH);
  const [wallThicknessDirection, setWallThicknessDirection] = useState<EDirection>(EDirection.LEFT);
  const [selectedSimpleMeasurement, setSelectedSimpleMeasurement] = useState<ISelectedSimpleMeasurement>(emptySelectedSimpleMeasurement);
  const [selectedModel2D, setSelectedModel2D] = useState<ISelectedModel2D>(emptySelectedModel2D);
  const [selectedHotspot, setSelectedHotspot] = useState<ISelectedHotspot>(emptySelectedHotspot);
  const { interactionMode } = useToolbarContext();

  const resetSelection = useCallback(() => {
    setSelectedStandardWall(emptyStandardWall);
    setSelectedPartitionWall(emptyPartitionWall);
    setSelectedWindow(emptySelectedWindow);
    setSelectedDoor(emptySelectedDoor);
    setSelectedPassage(emptySelectedPassage);
    setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
    setSelectedRadiator(emptySelectedRadiator);
    setSelectedProduct(emptyProduct);
    setSelectedElement(emptySelectedElement);
    setSelectedModel2D(emptySelectedModel2D);
    setSelectedHotspot(emptySelectedHotspot);
    setSelectedSpace(emptySelectedSpace);
    setActiveDrawer(EActiveDrawer.NONE);
  }, []);

  const resetSelectionWithoutHistoryDrawer = useCallback(() => {
    setSelectedStandardWall(emptyStandardWall);
    setSelectedPartitionWall(emptyPartitionWall);
    setSelectedWindow(emptySelectedWindow);
    setSelectedDoor(emptySelectedDoor);
    setSelectedPassage(emptySelectedPassage);
    setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
    setSelectedRadiator(emptySelectedRadiator);
    setSelectedProduct(emptyProduct);
    setSelectedElement(emptySelectedElement);
    setSelectedModel2D(emptySelectedModel2D);
    setActiveDrawer(activeDrawer === EActiveDrawer.HISTORY ? activeDrawer : EActiveDrawer.NONE);
    setSelectedSpace(emptySelectedSpace);
  }, [activeDrawer]);

  useEffect(() => {
    if (interactionMode !== EInteractionMode.MOVE) {
      resetSelection();
    }
  }, [interactionMode]);

  useEffect(() => {
    if (!selectionAllowed) {
      resetSelection();
    }
  }, [selectionAllowed, resetSelection]);

  const hasSelection = useCallback(
    () =>
      selectedPartitionWall !== emptyPartitionWall ||
      selectedStandardWall !== emptyStandardWall ||
      selectedWindow !== emptySelectedWindow ||
      selectedDoor !== emptySelectedDoor ||
      selectedPassage !== emptySelectedPassage ||
      selectedSimpleMeasurement !== emptySelectedSimpleMeasurement ||
      selectedSpace !== emptySelectedSpace ||
      selectedProduct !== emptyProduct ||
      selectedRadiator !== emptySelectedRadiator ||
      selectedElement !== emptySelectedElement ||
      selectedModel2D !== emptySelectedModel2D ||
      selectedHotspot !== emptySelectedHotspot ||
      activeDrawer !== EActiveDrawer.NONE,
    [
      selectedPartitionWall,
      selectedStandardWall,
      selectedWindow,
      selectedSimpleMeasurement,
      selectedProduct,
      selectedModel2D,
      selectedRadiator,
      selectedDoor,
      selectedPassage,
      selectedElement,
      selectedSpace,
      selectedHotspot,
      activeDrawer,
    ],
  );

  const hasSelectionWithoutDrawer = useCallback(
    () =>
      selectedPartitionWall !== emptyPartitionWall ||
      selectedStandardWall !== emptyStandardWall ||
      selectedWindow !== emptySelectedWindow ||
      selectedDoor !== emptySelectedDoor ||
      selectedPassage !== emptySelectedPassage ||
      selectedSimpleMeasurement !== emptySelectedSimpleMeasurement ||
      selectedSpace !== emptySelectedSpace ||
      selectedProduct !== emptyProduct ||
      selectedRadiator !== emptySelectedRadiator ||
      selectedElement !== emptySelectedElement ||
      selectedModel2D !== emptySelectedModel2D,
    [
      selectedPartitionWall,
      selectedStandardWall,
      selectedWindow,
      selectedSimpleMeasurement,
      selectedProduct,
      selectedModel2D,
      selectedRadiator,
      selectedDoor,
      selectedPassage,
      selectedElement,
      selectedSpace,
    ],
  );

  // TODO make this whole thing more efficient. this is so redundant

  // Handle room selection
  const handleSpaceClick = useCallback((spaceId: string) => {
    setSelectedSpace({ spaceId });
    setSelectedStandardWall(emptyStandardWall);
    setSelectedPartitionWall(emptyPartitionWall);
    setSelectedWindow(emptySelectedWindow);
    setSelectedDoor(emptySelectedDoor);
    setSelectedPassage(emptySelectedPassage);
    setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
    setSelectedProduct(emptyProduct);
    setSelectedRadiator(emptySelectedRadiator);
    setSelectedElement(emptySelectedElement);
    setSelectedModel2D(emptySelectedModel2D);
    setSelectedHotspot(emptySelectedHotspot);
    if (selectedSpace && selectedSpace.spaceId === spaceId) {
      setSelectedSpace(emptySelectedSpace);
      setActiveDrawer(EActiveDrawer.NONE);
    } else {
      setSelectedSpace({ spaceId });
      setActiveDrawer(EActiveDrawer.FLOOR);
    }
  }, [selectedSpace]);

  const handleWindowClick = useCallback(
    (wallIndex: number, windowIndex: number) => {
      setSelectedSpace(emptySelectedSpace);
      setSelectedStandardWall(emptyStandardWall);
      setSelectedPartitionWall(emptyPartitionWall);
      setSelectedDoor(emptySelectedDoor);
      setSelectedPassage(emptySelectedPassage);
      setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
      setSelectedProduct(emptyProduct);
      setSelectedRadiator(emptySelectedRadiator);
      setSelectedElement(emptySelectedElement);
      setSelectedModel2D(emptySelectedModel2D);
      setSelectedHotspot(emptySelectedHotspot);
      if (selectedWindow && selectedWindow.windowIndex === windowIndex && selectedWindow.wallIndex === wallIndex) {
        setSelectedWindow(emptySelectedWindow);
        setActiveDrawer(EActiveDrawer.NONE);
      } else {
        setSelectedWindow({ wallIndex, windowIndex });
        setActiveDrawer(EActiveDrawer.WINDOW);
      }
    },
    [selectedWindow],
  );

  const handleDoorClick = useCallback(
    (wallIndex: number, doorIndex: number) => {
      setSelectedSpace(emptySelectedSpace);
      setSelectedStandardWall(emptyStandardWall);
      setSelectedPartitionWall(emptyPartitionWall);
      setSelectedWindow(emptySelectedWindow);
      setSelectedPassage(emptySelectedPassage);
      setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
      setSelectedProduct(emptyProduct);
      setSelectedRadiator(emptySelectedRadiator);
      setSelectedElement(emptySelectedElement);
      setSelectedModel2D(emptySelectedModel2D);
      setSelectedHotspot(emptySelectedHotspot);
      if (selectedDoor && selectedDoor.doorIndex === doorIndex && selectedDoor.wallIndex === wallIndex) {
        setSelectedDoor(emptySelectedDoor);
        setActiveDrawer(EActiveDrawer.NONE);
      } else {
        setSelectedDoor({ wallIndex, doorIndex });
        setActiveDrawer(EActiveDrawer.DOOR);
      }
    },
    [selectedDoor],
  );

  const handlePassageClick = useCallback(
    (wallIndex: number, passageIndex: number) => {
      setSelectedSpace(emptySelectedSpace);
      setSelectedStandardWall(emptyStandardWall);
      setSelectedPartitionWall(emptyPartitionWall);
      setSelectedWindow(emptySelectedWindow);
      setSelectedDoor(emptySelectedDoor);
      setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
      setSelectedProduct(emptyProduct);
      setSelectedRadiator(emptySelectedRadiator);
      setSelectedElement(emptySelectedElement);
      setSelectedModel2D(emptySelectedModel2D);
      setSelectedHotspot(emptySelectedHotspot);
      if (selectedPassage && selectedPassage.passageIndex === passageIndex && selectedPassage.wallIndex === wallIndex) {
        setSelectedPassage(emptySelectedPassage);
        setActiveDrawer(EActiveDrawer.NONE);
      } else {
        setSelectedPassage({ wallIndex, passageIndex });
        setActiveDrawer(EActiveDrawer.PASSAGE);
      }
    },
    [selectedPassage],
  );

  const handleWallClick = useCallback(
    (wallIndex: number) => {
      setSelectedSpace(emptySelectedSpace);
      setSelectedWindow(emptySelectedWindow);
      setSelectedPartitionWall(emptyPartitionWall);
      setSelectedDoor(emptySelectedDoor);
      setSelectedPassage(emptySelectedPassage);
      setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
      setSelectedProduct(emptyProduct);
      setSelectedRadiator(emptySelectedRadiator);
      setSelectedElement(emptySelectedElement);
      setSelectedModel2D(emptySelectedModel2D);
      setSelectedHotspot(emptySelectedHotspot);
      if (selectedStandardWall.wallIndex === wallIndex) {
        setSelectedStandardWall(emptyStandardWall);
        setActiveDrawer(EActiveDrawer.NONE);
      } else {
        setSelectedStandardWall({ wallIndex: wallIndex });
        setActiveDrawer(EActiveDrawer.WALL);
      }
    },
    [selectedStandardWall],
  );

  const handlePartitionWallClick = useCallback(
    (wallIndex: number): void => {
      setSelectedSpace(emptySelectedSpace);
      setSelectedWindow(emptySelectedWindow);
      setSelectedDoor(emptySelectedDoor);
      setSelectedPassage(emptySelectedPassage);
      setSelectedStandardWall(emptyStandardWall);
      setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
      setSelectedProduct(emptyProduct);
      setSelectedRadiator(emptySelectedRadiator);
      setSelectedElement(emptySelectedElement);
      setSelectedModel2D(emptySelectedModel2D);
      setSelectedHotspot(emptySelectedHotspot);
      if (selectedPartitionWall.wallIndex === wallIndex) {
        setSelectedPartitionWall(emptyPartitionWall);
        setActiveDrawer(EActiveDrawer.NONE);
      } else {
        setSelectedPartitionWall({ wallIndex: wallIndex });
        setActiveDrawer(EActiveDrawer.PARTITION);
      }
    },
    [selectedPartitionWall],
  );

  const handleSimpleMeasurementClick = useCallback(
    (simpleMeasurementIndex: number): void => {
      setSelectedSpace(emptySelectedSpace);
      setSelectedWindow(emptySelectedWindow);
      setSelectedDoor(emptySelectedDoor);
      setSelectedPassage(emptySelectedPassage);
      setSelectedStandardWall(emptyStandardWall);
      setSelectedPartitionWall(emptyPartitionWall);
      setSelectedProduct(emptyProduct);
      setSelectedRadiator(emptySelectedRadiator);
      setSelectedElement(emptySelectedElement);
      setSelectedModel2D(emptySelectedModel2D);
      setSelectedHotspot(emptySelectedHotspot);
      if (selectedSimpleMeasurement.simpleMeasurementIndex === simpleMeasurementIndex) {
        setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
        setActiveDrawer(EActiveDrawer.NONE);
      } else {
        setSelectedSimpleMeasurement({ simpleMeasurementIndex: simpleMeasurementIndex });
        setActiveDrawer(EActiveDrawer.SIMPLE_MEASUREMENT);
      }
    },
    [selectedSimpleMeasurement],
  );

  const handleProductClick = useCallback(
    (productIndex: number): void => {
      setSelectedSpace(emptySelectedSpace);
      setSelectedWindow(emptySelectedWindow);
      setSelectedDoor(emptySelectedDoor);
      setSelectedPassage(emptySelectedPassage);
      setSelectedStandardWall(emptyStandardWall);
      setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
      setSelectedPartitionWall(emptyPartitionWall);
      setSelectedRadiator(emptySelectedRadiator);
      setSelectedElement(emptySelectedElement);
      setSelectedModel2D(emptySelectedModel2D);
      setSelectedHotspot(emptySelectedHotspot);
      if (selectedProduct.selectedProductIndex === productIndex) {
        setSelectedProduct(emptyProduct);
        setActiveDrawer(EActiveDrawer.NONE);
      } else {
        setSelectedProduct({ selectedProductIndex: productIndex });
        setActiveDrawer(EActiveDrawer.PRODUCTS);
      }
    },
    [selectedProduct],
  );
// Updated handleRadiatorClick implementation
const handleRadiatorClick = useCallback(
  (wallIndex: number, radiatorIndex: number, side: Side) => {
    setSelectedSpace(emptySelectedSpace);
    setSelectedStandardWall(emptyStandardWall);
    setSelectedPartitionWall(emptyPartitionWall);
    setSelectedDoor(emptySelectedDoor);
    setSelectedPassage(emptySelectedPassage);
    setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
    setSelectedProduct(emptyProduct);
    setSelectedRadiator(emptySelectedRadiator);
    setSelectedWindow(emptySelectedWindow);
    setSelectedElement(emptySelectedElement);
    setSelectedModel2D(emptySelectedModel2D);
    setSelectedHotspot(emptySelectedHotspot);
    if (selectedRadiator && 
        selectedRadiator.radiatorIndex === radiatorIndex && 
        selectedRadiator.wallIndex === wallIndex &&
        selectedRadiator.side === side) {
      setSelectedRadiator(emptySelectedRadiator);
      setActiveDrawer(EActiveDrawer.NONE);
    } else {
      setSelectedRadiator({ wallIndex, radiatorIndex, side });
      setActiveDrawer(EActiveDrawer.RADIATOR);
    }
  },
  [selectedRadiator],
);

  const handleElementClick = useCallback(
    (elementIndex: number) => {
      setSelectedSpace(emptySelectedSpace);
      setSelectedStandardWall(emptyStandardWall);
      setSelectedPartitionWall(emptyPartitionWall);
      setSelectedDoor(emptySelectedDoor);
      setSelectedPassage(emptySelectedPassage);
      setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
      setSelectedProduct(emptyProduct);
      setSelectedRadiator(emptySelectedRadiator);
      setSelectedWindow(emptySelectedWindow);
      setSelectedRadiator(emptySelectedRadiator);
      setSelectedModel2D(emptySelectedModel2D);
      setSelectedHotspot(emptySelectedHotspot);
      if (selectedElement.selectedElementIndex === elementIndex) {
        setSelectedElement(emptySelectedElement);
        setActiveDrawer(EActiveDrawer.NONE);
      } else {
        setSelectedElement({ selectedElementIndex: elementIndex });
        setActiveDrawer(EActiveDrawer.ELEMENT);
      }
    },
    [selectedElement],
  );

  const handleModel2DClick = useCallback(
    (model2DIndex: number) => {
      setSelectedSpace(emptySelectedSpace);
      setSelectedWindow(emptySelectedWindow);
      setSelectedDoor(emptySelectedDoor);
      setSelectedPassage(emptySelectedPassage);
      setSelectedStandardWall(emptyStandardWall);
      setSelectedPartitionWall(emptyPartitionWall);
      setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
      setSelectedProduct(emptyProduct);
      setSelectedRadiator(emptySelectedRadiator);
      setSelectedElement(emptySelectedElement);
      setSelectedModel2D(emptySelectedModel2D);
      setSelectedHotspot(emptySelectedHotspot);
      if (selectedModel2D.selectedModel2DIndex === model2DIndex) {
        setSelectedModel2D(emptySelectedModel2D);
        setActiveDrawer(EActiveDrawer.NONE);
      } else {
        setSelectedModel2D({ selectedModel2DIndex: model2DIndex });
        setActiveDrawer(EActiveDrawer.MODEL2D);
      }
    },
    [selectedModel2D],
  );

  const handleHotspotClick = useCallback(
    (hotspotId: number) => {
      setSelectedSpace(emptySelectedSpace);
      setSelectedWindow(emptySelectedWindow);
      setSelectedDoor(emptySelectedDoor);
      setSelectedPassage(emptySelectedPassage);
      setSelectedStandardWall(emptyStandardWall);
      setSelectedPartitionWall(emptyPartitionWall);
      setSelectedSimpleMeasurement(emptySelectedSimpleMeasurement);
      setSelectedProduct(emptyProduct);
      setSelectedRadiator(emptySelectedRadiator);
      setSelectedElement(emptySelectedElement);
      setSelectedModel2D(emptySelectedModel2D);
      if (selectedHotspot.hotspotId === hotspotId) {
        setSelectedHotspot(emptySelectedHotspot);
        setActiveDrawer(EActiveDrawer.NONE);
      } else {
        setSelectedHotspot({ hotspotId });
        setActiveDrawer(EActiveDrawer.ATTACHMENT);
      }
    },
    [selectedHotspot],
  );

  return {
    wallChangeDirection,
    setWallChangeDirection,
    selectedStandardWall,
    selectedPartitionWall,
    selectedSpace,
    selectedWindow,
    selectedDoor,
    selectedPassage,
    selectedSimpleMeasurement,
    selectedRadiator,
    selectedProduct,
    selectedElement,
    selectedModel2D,
    selectedHotspot,
    selectionAllowed,
    setSelectionAllowed,
    handleWallClick,
    handleSpaceClick,
    handleWindowClick,
    handleDoorClick,
    handlePassageClick,
    handlePartitionWallClick,
    handleSimpleMeasurementClick,
    handleProductClick,
    handleRadiatorClick,
    handleElementClick,
    handleModel2DClick,
    handleHotspotClick,
    activeDrawer,
    setActiveDrawer,
    resetSelection,
    resetSelectionWithoutHistoryDrawer,
    hasSelection,
    hasSelectionWithoutDrawer,
    wallThicknessDirection,
    setWallThicknessDirection,
  };
};
